// @ts-nocheck
/**
 * ツインキーバフマネージャー
 *
 * 仕様書: twin_key_status_skill_system_v1.md
 * 設計書: twin_key_phase2_design.md
 */

import {
  BUFF_TYPES,
  BUFF_META,
  BUFF_STACK_EFFECTS,
  BUFF_DEBUFF_CANCEL_MAP,
  DEBUFF_BUFF_CANCEL_MAP,
  getBuffMeta,
  getBuffName,
  getBuffIcon,
  getBuffStackEffect,
} from './buffTypes';
import { STATUS_EFFECT_TYPES } from '../statusEffects/statusTypes';

/**
 * バフマネージャークラス
 *
 * 責務:
 * - バフの付与・解除
 * - バフと状態異常の相殺処理
 * - ターン経過処理（再生、持続時間減少）
 * - バフによる効果計算
 */
class BuffManager {
  constructor() {
    // バフマネージャーはステートレス設計
  }

  /**
   * バフを付与
   *
   * @param {Object} monster - 対象モンスター
   * @param {string} buffType - バフタイプ
   * @param {Object} options - オプション
   * @param {number} options.duration - 持続ターン
   * @param {number} options.stack - 付与スタック数
   * @returns {{ success: boolean, monster: Object, message: string, canceled: Object|null }}
   */
  applyBuff(monster, buffType, options = {}) {
    const meta = getBuffMeta(buffType);
    if (!meta) {
      return { success: false, monster, message: '無効なバフタイプ', canceled: null };
    }

    const { duration, stack = 1 } = options;

    // バフ配列を初期化
    let buffs = [...(monster.buffs || [])];
    let statusEffects = [...(monster.statusEffects || [])];
    let canceledInfo = null;

    // 相殺処理: バフと対応する状態異常があれば相殺
    const cancelTarget = BUFF_DEBUFF_CANCEL_MAP[buffType];
    if (cancelTarget) {
      const debuffIndex = statusEffects.findIndex((s) => s.type === cancelTarget);
      if (debuffIndex >= 0) {
        const debuff = statusEffects[debuffIndex];
        const debuffStack = debuff.stack || 1;
        const incomingStack = stack;

        if (debuffStack > incomingStack) {
          // 状態異常が残る（スタック減少）
          statusEffects[debuffIndex] = {
            ...debuff,
            stack: debuffStack - incomingStack,
          };
          canceledInfo = {
            type: 'partial_cancel',
            debuffRemaining: debuffStack - incomingStack,
          };
          return {
            success: true,
            monster: { ...monster, buffs, statusEffects },
            message: `${monster.name}の${getBuffName(buffType)}が${cancelTarget}と相殺された！（残り${debuffStack - incomingStack}スタック）`,
            canceled: canceledInfo,
          };
        } else if (debuffStack < incomingStack) {
          // バフが残る（状態異常完全解除、バフ減少適用）
          statusEffects = statusEffects.filter((s) => s.type !== cancelTarget);
          const remainingBuffStack = incomingStack - debuffStack;
          canceledInfo = {
            type: 'buff_wins',
            buffRemaining: remainingBuffStack,
          };
          // 以下で残りスタック分のバフを適用
          return this._applyBuffWithStack(monster, buffType, remainingBuffStack, duration, buffs, statusEffects, canceledInfo);
        } else {
          // 完全相殺
          statusEffects = statusEffects.filter((s) => s.type !== cancelTarget);
          canceledInfo = { type: 'full_cancel' };
          return {
            success: true,
            monster: { ...monster, buffs, statusEffects },
            message: `${monster.name}の${getBuffName(buffType)}と${cancelTarget}が完全に相殺された！`,
            canceled: canceledInfo,
          };
        }
      }
    }

    // 相殺対象がない場合は通常のバフ付与
    return this._applyBuffWithStack(monster, buffType, stack, duration, buffs, statusEffects, null);
  }

  /**
   * @private
   * バフをスタック数で付与
   */
  _applyBuffWithStack(monster, buffType, stack, duration, buffs, statusEffects, canceledInfo) {
    const meta = getBuffMeta(buffType);

    // 既存の同タイプバフを検索
    const existingIndex = buffs.findIndex((b) => b.type === buffType);

    if (existingIndex >= 0) {
      const existing = buffs[existingIndex];
      if (meta.stackable) {
        const newStack = Math.min(existing.stack + stack, meta.maxStack);
        buffs[existingIndex] = {
          ...existing,
          stack: newStack,
          remainingTurns: duration ?? meta.defaultDuration,
        };
        return {
          success: true,
          monster: { ...monster, buffs, statusEffects },
          message: `${monster.name}の${meta.name}が${newStack}スタックになった！`,
          canceled: canceledInfo,
        };
      } else {
        buffs[existingIndex] = {
          ...existing,
          remainingTurns: duration ?? meta.defaultDuration,
        };
        return {
          success: true,
          monster: { ...monster, buffs, statusEffects },
          message: `${monster.name}の${meta.name}が延長された！`,
          canceled: canceledInfo,
        };
      }
    } else {
      buffs.push({
        type: buffType,
        stack: Math.min(stack, meta.maxStack),
        remainingTurns: duration ?? meta.defaultDuration,
      });
      return {
        success: true,
        monster: { ...monster, buffs, statusEffects },
        message: `${monster.name}は${meta.name}状態になった！`,
        canceled: canceledInfo,
      };
    }
  }

  /**
   * 状態異常付与時の相殺チェック（状態異常エンジンから呼ばれる）
   *
   * @param {Object} monster - 対象モンスター
   * @param {string} debuffType - 付与しようとしている状態異常タイプ
   * @param {number} debuffStack - 付与スタック数
   * @returns {{ monster: Object, remainingDebuffStack: number, message: string }}
   */
  checkDebuffCancellation(monster, debuffType, debuffStack = 1) {
    const cancelBuff = DEBUFF_BUFF_CANCEL_MAP[debuffType];
    if (!cancelBuff) {
      return { monster, remainingDebuffStack: debuffStack, message: '' };
    }

    const buffs = monster.buffs || [];
    const buffIndex = buffs.findIndex((b) => b.type === cancelBuff);

    if (buffIndex < 0) {
      return { monster, remainingDebuffStack: debuffStack, message: '' };
    }

    const buff = buffs[buffIndex];
    const buffStack = buff.stack || 1;

    if (buffStack > debuffStack) {
      // バフが残る
      const newBuffs = [...buffs];
      newBuffs[buffIndex] = { ...buff, stack: buffStack - debuffStack };
      return {
        monster: { ...monster, buffs: newBuffs },
        remainingDebuffStack: 0,
        message: `${getBuffName(cancelBuff)}と相殺（バフ残り${buffStack - debuffStack}）`,
      };
    } else if (buffStack < debuffStack) {
      // 状態異常が残る
      const newBuffs = buffs.filter((b) => b.type !== cancelBuff);
      return {
        monster: { ...monster, buffs: newBuffs },
        remainingDebuffStack: debuffStack - buffStack,
        message: `${getBuffName(cancelBuff)}と相殺（状態異常残り${debuffStack - buffStack}）`,
      };
    } else {
      // 完全相殺
      const newBuffs = buffs.filter((b) => b.type !== cancelBuff);
      return {
        monster: { ...monster, buffs: newBuffs },
        remainingDebuffStack: 0,
        message: `${getBuffName(cancelBuff)}と完全相殺`,
      };
    }
  }

  /**
   * バフを解除
   *
   * @param {Object} monster - 対象モンスター
   * @param {string} buffType - 解除するバフタイプ
   * @returns {{ monster: Object, removed: boolean, message: string }}
   */
  removeBuff(monster, buffType) {
    const buffs = monster.buffs || [];
    const existingIndex = buffs.findIndex((b) => b.type === buffType);

    if (existingIndex < 0) {
      return { monster, removed: false, message: '' };
    }

    const meta = getBuffMeta(buffType);
    const newBuffs = buffs.filter((b) => b.type !== buffType);

    return {
      monster: { ...monster, buffs: newBuffs },
      removed: true,
      message: `${monster.name}の${meta?.name || buffType}が解除された！`,
    };
  }

  /**
   * 全バフを解除（戦闘終了時など）
   */
  clearAllBuffs(monster) {
    return {
      ...monster,
      buffs: [],
    };
  }

  /**
   * ターン終了時処理
   * - 再生回復
   * - 持続時間減少
   *
   * @param {Object} monster - モンスター
   * @returns {{ monster: Object, heal: number, messages: string[], removed: string[] }}
   */
  processTurnEnd(monster) {
    if (!monster.buffs || monster.buffs.length === 0) {
      return { monster, heal: 0, messages: [], removed: [] };
    }

    let totalHeal = 0;
    const messages = [];
    const removed = [];
    let buffs = [...monster.buffs];
    let currentHp = monster.currentHp;
    const maxHp = monster.maxHp || monster.hp;

    // 再生処理
    const regenBuff = buffs.find((b) => b.type === BUFF_TYPES.REGEN);
    if (regenBuff) {
      const effect = getBuffStackEffect(BUFF_TYPES.REGEN, regenBuff.stack);
      const healAmount = Math.floor(maxHp * effect.hpPercent);
      totalHeal = healAmount;
      currentHp = Math.min(maxHp, currentHp + healAmount);
      messages.push(`${monster.name}は再生で${healAmount}回復した！`);
    }

    // 持続時間減少
    buffs = buffs.map((buff) => {
      if (buff.remainingTurns > 0) {
        return { ...buff, remainingTurns: buff.remainingTurns - 1 };
      }
      return buff;
    });

    // 持続時間切れのバフを解除
    buffs = buffs.filter((buff) => {
      if (buff.remainingTurns === 0) {
        const meta = getBuffMeta(buff.type);
        removed.push(buff.type);
        messages.push(`${monster.name}の${meta?.name || buff.type}が切れた！`);
        return false;
      }
      return true;
    });

    return {
      monster: {
        ...monster,
        currentHp,
        buffs,
      },
      heal: totalHeal,
      messages,
      removed,
    };
  }

  // ==================== 効果計算メソッド ====================

  /**
   * 攻撃力補正を計算
   *
   * @param {Object} monster - モンスター
   * @returns {number} 攻撃力倍率（1.0 = 100%）
   */
  getAttackMultiplier(monster) {
    if (!monster.buffs) return 1.0;

    let multiplier = 1.0;

    const atkBuff = monster.buffs.find((b) => b.type === BUFF_TYPES.ATK_UP);
    if (atkBuff) {
      const effect = getBuffStackEffect(BUFF_TYPES.ATK_UP, atkBuff.stack);
      multiplier *= effect.atkMultiplier;
    }

    return multiplier;
  }

  /**
   * 防御補正を計算
   *
   * @param {Object} monster - モンスター
   * @returns {number} 防御増加値（加算）
   */
  getDefenseModifier(monster) {
    if (!monster.buffs) return 0;

    let modifier = 0;

    const defBuff = monster.buffs.find((b) => b.type === BUFF_TYPES.DEF_UP);
    if (defBuff) {
      const effect = getBuffStackEffect(BUFF_TYPES.DEF_UP, defBuff.stack);
      modifier += effect.def;
    }

    return modifier;
  }

  /**
   * 速度補正を計算
   *
   * @param {Object} monster - モンスター
   * @returns {number} 速度増加値（加算）
   */
  getSpeedModifier(monster) {
    if (!monster.buffs) return 0;

    let modifier = 0;

    const spdBuff = monster.buffs.find((b) => b.type === BUFF_TYPES.SPD_UP);
    if (spdBuff) {
      const effect = getBuffStackEffect(BUFF_TYPES.SPD_UP, spdBuff.stack);
      modifier += effect.speed;
    }

    return modifier;
  }

  /**
   * クリティカル率補正を計算
   *
   * @param {Object} monster - モンスター
   * @returns {number} クリティカル率増加値（加算）
   */
  getCritRateModifier(monster) {
    if (!monster.buffs) return 0;

    let modifier = 0;

    const critBuff = monster.buffs.find((b) => b.type === BUFF_TYPES.CRIT_UP);
    if (critBuff) {
      const effect = getBuffStackEffect(BUFF_TYPES.CRIT_UP, critBuff.stack);
      modifier += effect.critRate;
    }

    return modifier;
  }

  /**
   * 回避率を計算
   *
   * @param {Object} monster - モンスター
   * @returns {number} 回避率（0.0 ~ 1.0）
   */
  getEvasionRate(monster) {
    if (!monster.buffs) return 0;

    const evasionBuff = monster.buffs.find((b) => b.type === BUFF_TYPES.EVASION_UP);
    if (evasionBuff) {
      const effect = getBuffStackEffect(BUFF_TYPES.EVASION_UP, evasionBuff.stack);
      return effect.evasion;
    }

    return 0;
  }

  /**
   * 反撃判定
   *
   * @param {Object} monster - モンスター
   * @returns {{ hasCounter: boolean, damageRate: number }}
   */
  checkCounter(monster) {
    if (!monster.buffs) return { hasCounter: false, damageRate: 0 };

    const counterBuff = monster.buffs.find((b) => b.type === BUFF_TYPES.COUNTER);
    if (counterBuff) {
      const effect = getBuffStackEffect(BUFF_TYPES.COUNTER, 1);
      return {
        hasCounter: Math.random() < effect.counterRate,
        damageRate: effect.counterDamage,
      };
    }

    return { hasCounter: false, damageRate: 0 };
  }

  /**
   * 特定のバフを持っているか確認
   */
  hasBuff(monster, buffType) {
    if (!monster.buffs) return false;
    return monster.buffs.some((b) => b.type === buffType);
  }

  /**
   * バフのスタック数を取得
   */
  getBuffStack(monster, buffType) {
    if (!monster.buffs) return 0;
    const buff = monster.buffs.find((b) => b.type === buffType);
    return buff?.stack || 0;
  }

  /**
   * モンスターのバフサマリーを取得（UI表示用）
   *
   * @param {Object} monster - モンスター
   * @returns {Array<{ type: string, name: string, icon: string, stack: number, turns: number }>}
   */
  getBuffSummary(monster) {
    if (!monster.buffs) return [];

    return monster.buffs.map((buff) => {
      const meta = getBuffMeta(buff.type);
      return {
        type: buff.type,
        name: meta?.name || buff.type,
        icon: getBuffIcon(buff.type),
        stack: buff.stack,
        turns: buff.remainingTurns,
      };
    });
  }
}

// シングルトンインスタンス
export const buffManager = new BuffManager();
export default BuffManager;
